import { forwardRef, useEffect, useState } from 'react'
import {
  AutocompleteInput,
  BooleanInput,
  ReferenceInput,
  required,
  useGetList,
  useNotify,
  useRecordContext,
} from 'react-admin'
import { FormatButtons, RichTextInputToolbar as RaRichTextInputToolbar, RichTextInput } from 'ra-input-rich-text'
import { useController, useFormContext } from 'react-hook-form'
import { Autocomplete, FormControlLabel, Switch, Typography } from '@mui/material'
import NumberFormat from 'react-number-format'
import MuiTextField from '@mui/material/TextField'
import { gql, useMutation, useQuery } from '@apollo/client'

import { FilledInput, FormControl, FormHelperText, InputAdornment, InputLabel } from '../../../components'

export {
  AutocompleteInput as Autocomplete,
  AutocompleteArrayInput as AutocompleteArray,
  BooleanInput as Boolean,
  DateInput as Date,
  DateTimeInput as DateTime,
  ImageInput as Image,
  NumberInput as Number,
  ReferenceArrayInput as ReferenceArray,
  ReferenceInput as Reference,
  SelectArrayInput as SelectArray,
  SelectInput as Select,
  TextInput as Text,
  RadioButtonGroupInput as RadioButtonGroup,
} from 'react-admin'

export function MuiText(props) {
  return <MuiTextField {...props} />
}

export function RichTextInputToolbar({ size = 'sm' }) {
  return (
    <RaRichTextInputToolbar sx={{ alignSelf: 'flex-start' }}>
      <FormatButtons size={size} />
    </RaRichTextInputToolbar>
  )
}

export function RichText({ size = 'sm', ...props }) {
  return <RichTextInput variant="" size={size} toolbar={<RichTextInputToolbar size="size" />} {...props} />
}

export function Fund({ source, isRequired = true, filter, ...props }) {
  const formatFund = (fund) => {
    if (!fund || typeof fund !== 'object') return ''
    return `${fund.number} - ${fund.name} - ${fund.userName}`
  }

  return (
    <ReferenceInput source={source ?? 'fundId'} reference="MFund" filter={filter}>
      <AutocompleteInput
        optionText={formatFund}
        inputText={formatFund}
        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
        validate={[isRequired && required()]}
        fullWidth
        parse={(value) => (value === '' ? null : value)}
        {...props}
      />
    </ReferenceInput>
  )
}

export function Charity({ source, isRequired = true, ...props }) {
  const formatCharity = (charity) => {
    if (!charity || typeof charity !== 'object') return ''
    return `${charity.id} - ${charity.accountName}`
  }

  return (
    <ReferenceInput source={source ?? 'charityId'} reference="Charity">
      <AutocompleteInput
        optionText={formatCharity}
        inputText={formatCharity}
        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
        validate={[isRequired && required()]}
        fullWidth
        {...props}
      />
    </ReferenceInput>
  )
}

export function User({ source, isRequired = true, ...props }) {
  const formatUser = (user) => {
    if (!user || typeof user !== 'object') return ''
    return user.email ? `${user.name} (${user.email})` : user.name
  }

  return (
    <ReferenceInput source={source ?? 'userId'} reference="User">
      <AutocompleteInput
        optionText={formatUser}
        inputText={formatUser}
        isOptionEqualToValue={(option, value) => {
          const optionId = option?.id
          const valueId = typeof value === 'object' ? value?.id : value
          return String(optionId) === String(valueId)
        }}
        validate={[isRequired && required()]}
        fullWidth
        parse={(value) => {
          // Convert to string ID to match User GET_MANY response format
          if (value === '' || value === null || value === undefined) return null
          const id = typeof value === 'object' ? value?.id : value
          return String(id)
        }}
        {...props}
      />
    </ReferenceInput>
  )
}

export function UserFund({ source, isRequired = true, filter, ...props }) {
  const formatUserFund = (user) => {
    if (!user || typeof user !== 'object') return ''
    const fund = user?.funds?.[0]
    const portalName = user?.fundProfiles?.[0]?.portalName ?? fund?.fundProfile?.portalName ?? 'No Portal'
    return `${portalName} - ${fund?.number ?? '<Fund Number Not Found>'} - ${fund?.name ?? 'Fund Name Not Found'} - ${
      user?.name ?? 'User Name Not Found'
    }`
  }

  return (
    <ReferenceInput source={source ?? 'userId'} reference="User" filter={filter}>
      <AutocompleteInput
        optionText={formatUserFund}
        inputText={formatUserFund}
        isOptionEqualToValue={(option, value) => {
          const optionId = option?.id
          const valueId = typeof value === 'object' ? value?.id : value
          return String(optionId) === String(valueId)
        }}
        validate={[isRequired && required()]}
        fullWidth
        parse={(value) => {
          // Convert to string ID to match User GET_MANY response format
          if (value === '' || value === null || value === undefined) return null
          const id = typeof value === 'object' ? value?.id : value
          return String(id)
        }}
        {...props}
      />
    </ReferenceInput>
  )
}

export function PortalInput({
  userSource = 'userId',
  fundSource = 'fundId',
  label = 'Portal',
  isRequired = false,
  fullWidth = false,
  resource = 'DonorPortal',
}) {
  const { setValue } = useFormContext()
  const record = useRecordContext()
  const [searchText, setSearchText] = useState('')
  const [selectedPortal, setSelectedPortal] = useState(null)

  const currentFundId = record?.[fundSource]

  const { data: initialPortals = [] } = useGetList(
    resource,
    {
      pagination: { page: 1, perPage: 1 },
      sort: { field: 'id', order: 'desc' },
      filter: currentFundId ? { fundId: currentFundId } : {},
    },
    { enabled: !!currentFundId && !selectedPortal }
  )

  const { data: searchPortals = [] } = useGetList(resource, {
    pagination: { page: 1, perPage: 25 },
    sort: { field: 'portalName', order: 'asc' },
    filter: searchText ? { q: searchText } : {},
  })

  useEffect(() => {
    if (!selectedPortal && initialPortals.length > 0) {
      setSelectedPortal(initialPortals[0])
    }
  }, [initialPortals, selectedPortal])

  const formatPortal = (portal) => {
    if (!portal || typeof portal !== 'object') return ''
    const name =
      (portal.user?.isOrganization && portal.user?.corporateName) ||
      (portal.user?.profile
        ? `${portal.user.profile.firstName ?? ''} ${portal.user.profile.lastName ?? ''}`.trim()
        : portal.user?.email ?? '')
    return `${portal.portalName ?? 'No Portal'} - ${portal.fund?.number ?? ''} - ${portal.fund?.name ?? ''} - ${name}`
  }

  let options = searchPortals
  if (!searchText && selectedPortal) {
    options = [selectedPortal, ...searchPortals.filter((p) => p.id !== selectedPortal.id)]
  }

  return (
    <Autocomplete
      value={selectedPortal}
      options={options}
      getOptionLabel={formatPortal}
      filterOptions={(x) => x}
      isOptionEqualToValue={(opt, val) => opt?.id === val?.id}
      fullWidth={fullWidth}
      onInputChange={(_, value, reason) => {
        if (reason === 'input') setSearchText(value)
      }}
      onChange={(_, portal) => {
        setSelectedPortal(portal)
        setValue(userSource, portal?.userId ?? null, { shouldDirty: true })
        setValue(fundSource, portal?.fundId ?? null, { shouldDirty: true })
      }}
      renderInput={(params) => (
        <MuiTextField
          {...params}
          label={label}
          required={isRequired}
          variant="filled"
          size="small"
          helperText="Portal Name - Fund # - Fund Name - User Name"
        />
      )}
      sx={{ mb: 2 }}
    />
  )
}

// Deliberately no `fixedDecimalScale` -- verified (isolated jest repro,
// react-number-format 4.9.4) that combined with decimalScale-only mode (no
// `format` mask) it breaks live typing: digits get absorbed into the fixed
// decimal places instead of the whole-number part, e.g. typing "100" from
// an empty field ends up as "1.00", and with allowEmptyFormatting also on
// (as below) the field gets permanently stuck showing ".00" and stops
// accepting input entirely. Currency values that need to always show .00
// should use formatCurrency() at the DISPLAY layer instead of forcing it
// into this live-typing input.
export const NumberFormatInput = forwardRef((props, ref) => {
  const { onChange, ...other } = props
  return (
    <NumberFormat
      {...other}
      getInputRef={ref}
      isNumericString={false}
      onValueChange={(values) => {
        onChange({
          target: {
            name: props.name,
            value: values.floatValue || 0,
          },
        })
      }}
      mask
      allowEmptyFormatting
      allowLeadingZeros={false}
      thousandSeparator
      displayType="input"
      decimalScale={2}
    />
  )
})

export function Amount({ name = '', label = '', isRequired = true }) {
  const {
    field,
    fieldState: { isTouched, invalid, error },
    formState: { isSubmitted },
  } = useController({
    name,
    rules: { required: isRequired ? 'Amount is required' : false },
  })
  return (
    <FormControl variant="filled" error={(isTouched || isSubmitted) && invalid} sx={{ mb: 4 }}>
      <InputLabel id={`${name}-label`} htmlFor={name}>
        {label}
      </InputLabel>
      <FilledInput
        name={name}
        id={name}
        aria-describedby={`${name}-label`}
        inputComponent={NumberFormatInput}
        startAdornment={<InputAdornment position="start">$</InputAdornment>}
        {...field}
      />
      {(isTouched || isSubmitted) && invalid && <FormHelperText id="my-helper-text">{error.message}</FormHelperText>}
    </FormControl>
  )
}

export function Branding({ source = 'brandingId', isRequired = true, ...props }) {
  const formatBranding = (record) => {
    if (!record || typeof record !== 'object') return ''
    return `${record.id} - ${record.displayName} (${record.brandingKey})`
  }

  return (
    <ReferenceInput source={source} reference="Branding">
      <AutocompleteInput
        optionText={formatBranding}
        inputText={formatBranding}
        isOptionEqualToValue={(option, value) => String(option?.id) === String(value?.id)}
        validate={[isRequired && required()]}
        fullWidth
        {...props}
      />
    </ReferenceInput>
  )
}

export function BlockToggle({ label = 'Block User', ...props }) {
  const TOGGLE_USER_BLOCKED = gql`
    mutation ToggleUserBlocked($userId: Int!, $blocked: Boolean!) {
      toggleUserBlocked(userId: $userId, blocked: $blocked) {
        id
        status
      }
    }
  `

  const GET_USER_BLOCKED = gql`
    query GetUserBlocked($id: Int!) {
      getUserBlocked(id: $id)
    }
  `

  const record = useRecordContext()
  const notify = useNotify()

  const { data: blockedData, loading: queryLoading } = useQuery(GET_USER_BLOCKED, {
    variables: { id: record.id },
  })

  const [isBlocked, setIsBlocked] = useState(false)

  useEffect(() => {
    if (blockedData !== undefined) {
      setIsBlocked(blockedData.getUserBlocked)
    }
  }, [blockedData])

  const [toggleUserBlocked, { loading: mutationLoading, error }] = useMutation(TOGGLE_USER_BLOCKED, {
    refetchQueries: [{ query: GET_USER_BLOCKED, variables: { id: record.id } }],
    onCompleted: () => {
      notify(isBlocked ? 'User has been blocked' : 'User has been unblocked', { type: 'success' })
    },
    onError: (blockError) => {
      notify(`Error: ${blockError.message}`, { type: 'error' })
      setIsBlocked(!isBlocked)
    },
  })

  const handleToggle = (event) => {
    const newBlocked = event.target.checked
    setIsBlocked(newBlocked)

    toggleUserBlocked({
      variables: {
        userId: record.id,
        blocked: newBlocked,
      },
    })
  }

  const isLoading = queryLoading || mutationLoading

  return (
    <FormControl error={!!error} sx={{ mb: 2 }}>
      <FormControlLabel
        control={<Switch checked={isBlocked} onChange={handleToggle} color="primary" disabled={isLoading} />}
        label={
          <Typography variant="body2">
            {label}
            <Typography variant="caption" display="block" color="text.secondary">
              {isBlocked ? 'User is currently blocked' : 'User has normal access'}
            </Typography>
          </Typography>
        }
      />
      {error && <FormHelperText>{error.message}</FormHelperText>}
    </FormControl>
  )
}
